/**
 * Privacy / AI data handling guide (ES + EN).
 */

export default [
  {
    slug: 'privacidad-ia-cv',
    date: '2026-08-17',
    category: 'privacy',
    related: ['como-hacer-un-cv-con-chatgpt', 'chatgpt-o-clarcv-para-tu-cv', 'cv-para-filtros-ats'],
    es: {
      title: 'Privacidad al usar IA en el CV: qué datos envías y qué no',
      description:
        '¿Qué pasa con tu currículum cuando usas IA? Qué datos envías, qué no guardamos y cómo revisar tu CV con ClarCV sin tarjeta ni registro obligatorio.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Subir un currículum a una web con IA genera una pregunta justa: ¿quién lee mis datos y para qué? Un CV no es un comentario anónimo. Lleva nombre, trayectoria, a veces teléfono, email y foto. En ClarCV lo tratamos como dato personal, no como “texto para entrenar un modelo”.',
        },
        {
          type: 'p',
          text: 'Esta guía explica, en concreto, qué ocurre cuando pulsas analizar o generar un CV nuevo: qué se envía, a quién, para qué, qué no se hace con ese contenido, y qué se queda en tu cuenta para que puedas editar y descargar. También enlazamos la política de privacidad, que es el texto legal. Aquí va el resumen en lenguaje claro.',
        },
        {
          type: 'h2',
          text: 'La frase corta (lo que prometemos)',
        },
        {
          type: 'ul',
          items: [
            'Se envía el contenido necesario de tu CV a un proveedor de IA (OpenAI, vía API) solo para responder a tu petición: análisis ATS o reescritura para plantilla.',
            'Ese contenido no se usa para entrenar modelos de IA de propósito general.',
            'No vendemos tus datos ni los usamos para publicidad dirigida.',
            'No pegamos tu CV en ChatGPT de consumo (chatgpt.com). Va por API de empresa, con contrato de encargado del tratamiento (DPA).',
            'ClarCV sí guarda lo necesario para prestarte el servicio (cuenta, archivo, resultados) mientras tu cuenta esté activa. Puedes pedir acceso o borrado en privacy@clarcv.com.',
          ],
        },
        {
          type: 'p',
          text: 'Ojo con el matiz “no se almacena”: OpenAI procesa la petición para devolverte el resultado. No lo usamos nosotros ni autorizamos usarlo para entrenar ChatGPT. ClarCV sí necesita guardar tu cuenta y el CV que subes; si no, no podrías volver al editor ni descargar el PDF. “No se almacena en absoluto en ningún servidor” sería mentir. “No se usa para entrenar ni se vende” es lo que podemos afirmar y lo que configuramos.',
        },
        {
          type: 'h2',
          text: 'Qué se envía (y qué no)',
        },
        {
          type: 'p',
          text: 'Cuando aceptas el consentimiento y lanzas un análisis o una reescritura, el backend extrae el texto del PDF o Word y envía ese contenido a OpenAI. Antes, cuando es posible, reducimos identificadores de contacto en la copia que viaja a la IA (email, teléfono, enlaces). No enviamos tu contraseña ni los datos de pago. Stripe gestiona el cobro de Pro; las tarjetas no pasan por nuestros servidores.',
        },
        {
          type: 'p',
          text: 'Si el PDF es una imagen (escaneo o exportado tapado), a veces hay que leer la página como foto para extraer texto. Sigue siendo para tu petición, no para un dataset público. Si no quieres que viaje una foto de perfil, no la subas o usa un archivo sin imagen: el producto ya oculta el óvalo vacío en plantillas cuando no hay foto.',
        },
        {
          type: 'h2',
          text: 'Por qué hace falta enviarlo',
        },
        {
          type: 'p',
          text: 'Un filtro ATS y una reescritura útil necesitan el texto real: puestos, fechas, logros, skills. Un modelo que no ve tu experiencia solo puede devolver consejos genéricos (“usa verbos de acción”). ClarCV intenta lo contrario: señalar huecos de ESTE CV. Eso implica transmitir contenido. El consentimiento explícito existe precisamente por eso: no hay análisis con IA en silencio.',
        },
        {
          type: 'p',
          text: 'Si no marcas la casilla, no se llama a la IA. Puedes leer las guías del blog, ver precios y plantillas, y no enviar ningún currículum.',
        },
        {
          type: 'h2',
          text: 'Entrenamiento de modelos: qué hemos cerrado',
        },
        {
          type: 'p',
          text: 'La API de OpenAI, en uso de pago / plataforma, no usa por defecto los datos de clientes para entrenar modelos de propósito general. Además, en la organización de ClarCV está desactivado el intercambio de inputs/outputs para mejorar modelos, y hay un Data Processing Addendum (DPA) firmado: OpenAI actúa como encargado; ClarCV como responsable. En la práctica: tu CV sirve para generar TU informe o TU plantilla, no para “enseñar” a ChatGPT con tu carrera.',
        },
        {
          type: 'p',
          text: 'Eso no convierte a ninguna IA en un notario. Revisa siempre el texto generado (nombres de empresas, cifras, tecnologías) antes de enviarlo a un proceso de selección. La IA puede suavizar o inventar un detalle; tú firmas la verdad.',
        },
        {
          type: 'h2',
          text: 'Qué guarda ClarCV en tu cuenta',
        },
        {
          type: 'p',
          text: 'Para que el producto funcione entre sesiones: email (cuenta), el archivo o el contenido extraído, resultados de análisis, plantilla elegida e historial de descargas según el plan. Eso no es “OpenAI se queda el CV para siempre”; es tu espacio de trabajo. Si cierras la cuenta o pides borrado, tratamos la solicitud según la política de privacidad y las obligaciones legales (facturación, etc.).',
        },
        {
          type: 'ul',
          items: [
            'Cuenta: correo y contraseña en hash.',
            'Producto: CV subido, análisis, contenido editable, PDF generado.',
            'Pago: Stripe (no almacenamos el número de tarjeta).',
            'Medición: Google Analytics 4 de forma agregada (páginas, no el texto del CV).',
          ],
        },
        {
          type: 'h2',
          text: 'Cómo comprobarlo tú mismo en ClarCV',
        },
        {
          type: 'p',
          text: 'Antes de analizar verás una casilla de consentimiento que nombra a OpenAI y deja claro el propósito (análisis o mejora), no el entrenamiento. En el pie de la web está la política de privacidad, con una sección de inteligencia artificial. Si algo no encaja con lo que lees aquí, el correo es privacy@clarcv.com. Preferimos una pregunta incómoda a un texto ambiguo.',
        },
        {
          type: 'h2',
          text: 'Consejos si te preocupa el dato (aunque uses ClarCV u otra herramienta)',
        },
        {
          type: 'ul',
          items: [
            'No hace falta el DNI, la dirección exacta ni el número de la seguridad social en un CV enviado a un portal.',
            'Una foto es opcional en muchos mercados; si no aporta, quítala.',
            'Usa un PDF o Word con texto seleccionable, no un escaneo de un papel con datos extra.',
            'Lee el consentimiento. Si una web no dice a quién envía el CV, no la uses para un documento con teléfono y email.',
            'Guarda tú el original. La herramienta es un editor, no el único archivo de tu vida laboral.',
          ],
        },
        {
          type: 'h2',
          text: 'Resumen para decidir con calma',
        },
        {
          type: 'p',
          text: 'ClarCV envía el CV a OpenAI para hacer el trabajo que pediste. No lo vendemos. No autorizamos usarlo para entrenar modelos. Guardamos lo de tu cuenta para que el flujo (editar, plantilla, PDF) exista. Eso es “seguro” en el sentido de transparente y acotado, no en el sentido de magia: internet implica un proveedor. Si ese intercambio no te encaja, no subas el archivo; las plantillas y las guías siguen siendo útiles sin IA.',
        },
        {
          type: 'faq',
          items: [
            {
              q: '¿OpenAI entrena ChatGPT con mi CV?',
              a: 'No está autorizado. Usamos la API, con intercambio para entrenamiento desactivado y un DPA. El contenido sirve para tu análisis o tu mejora, no para entrenar modelos generales.',
            },
            {
              q: '¿ClarCV vende mis datos?',
              a: 'No. No vendemos datos personales ni el contenido del currículum.',
            },
            {
              q: '¿Desaparece el archivo en cuanto termina el análisis?',
              a: 'No necesariamente. Lo guardamos en tu cuenta para que puedas editar y descargar. Puedes pedir borrado en privacy@clarcv.com.',
            },
            {
              q: '¿Puedo usar ClarCV sin enviar el CV a la IA?',
              a: 'Sí: no marques el consentimiento y no lances análisis. Crear desde cero o leer guías no obliga a un envío a OpenAI.',
            },
            {
              q: '¿Dónde está el texto legal?',
              a: 'En la Política de privacidad (sección de inteligencia artificial) y en los Términos de uso. Esta guía es la explicación en claro; el contrato con OpenAI es el DPA de encargado.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
    en: {
      title: 'Privacy when using AI on your CV: what you share and what you do not',
      description:
        'What happens to your résumé when you use AI? What data is sent, what we do not store, and how to review your CV with ClarCV — no card, no mandatory signup.',
      readMinutes: 12,
      sections: [
        {
          type: 'p',
          text: 'Uploading a CV to an AI product is a fair privacy question. A résumé is not an anonymous comment. It includes your name, career, and sometimes phone, email, and a photo. ClarCV treats it as personal data, not as “text to train a model”.',
        },
        {
          type: 'p',
          text: 'This guide explains what happens when you run analysis or generate a new CV: what is sent, to whom, for what, what is not done with that content, and what stays in your account so you can edit and download. The privacy policy is the legal text. This is the plain-language version.',
        },
        {
          type: 'h2',
          text: 'The short promise',
        },
        {
          type: 'ul',
          items: [
            'We send the CV content needed to an AI provider (OpenAI, via API) only to fulfil your request: ATS analysis or a rewrite into a template.',
            'That content is not used to train general-purpose AI models.',
            'We do not sell your data or use it for targeted ads.',
            'We do not paste your CV into consumer ChatGPT (chatgpt.com). It goes through the business API, with a data-processing addendum (DPA).',
            'ClarCV does store what the product needs (account, file, results) while your account is active. You can request access or deletion at privacy@clarcv.com.',
          ],
        },
        {
          type: 'p',
          text: 'Be precise about “not stored”. OpenAI processes the request to return a result. We do not use it to train models and we do not authorize that use. ClarCV still needs to keep your account and uploaded CV; otherwise the editor and PDF download cannot exist. “Nothing is stored on any server” would be false. “Not used for training and not sold” is what we configure and can state.',
        },
        {
          type: 'h2',
          text: 'What is sent (and what is not)',
        },
        {
          type: 'p',
          text: 'When you accept consent and start analysis or a rewrite, the backend extracts text from the PDF or Word file and sends that content to OpenAI. Where possible we reduce contact identifiers in the copy sent to the model (email, phone, profile links). We do not send your password or payment details. Stripe handles Pro checkout; card numbers do not sit on our servers.',
        },
        {
          type: 'p',
          text: 'If the PDF is an image (scan or flattened redaction), we may read the page as a picture to recover text. That is still for your request, not a public dataset. If you do not want a profile photo to travel, omit it or upload a file without an image.',
        },
        {
          type: 'h2',
          text: 'Why sending is required',
        },
        {
          type: 'p',
          text: 'Useful ATS feedback and rewriting need the real text: roles, dates, achievements, skills. A model that cannot see your experience can only give generic advice. ClarCV tries to comment on THIS CV. That means transmitting content. Explicit consent exists for that reason: there is no silent AI analysis.',
        },
        {
          type: 'p',
          text: 'If you do not tick the box, the AI is not called. You can still read the guides, view pricing and templates, and upload nothing.',
        },
        {
          type: 'h2',
          text: 'Model training: what we locked down',
        },
        {
          type: 'p',
          text: 'OpenAI’s platform API does not, by default, use customer API data to train general-purpose models. ClarCV’s organization also has input/output sharing for model improvement turned off, and a signed DPA: OpenAI is the processor; ClarCV is the controller. In practice your CV is used to produce YOUR report or YOUR template, not to “teach” ChatGPT your career.',
        },
        {
          type: 'p',
          text: 'That does not make AI a notary. Always review generated text (employer names, figures, tools) before you apply. Models can soften or invent a detail; you own the facts.',
        },
        {
          type: 'h2',
          text: 'What ClarCV keeps in your account',
        },
        {
          type: 'p',
          text: 'So the product works across sessions: email (account), the file or extracted content, analysis results, chosen template, and download history depending on plan. That is your workspace, not “OpenAI keeps the CV forever for training”. If you close the account or request deletion, we handle it under the privacy policy and legal duties (billing records, and so on).',
        },
        {
          type: 'ul',
          items: [
            'Account: email and hashed password.',
            'Product: uploaded CV, analysis, editable content, generated PDF.',
            'Payment: Stripe (we do not store the card number).',
            'Measurement: Google Analytics 4 in aggregate (pages, not CV body text).',
          ],
        },
        {
          type: 'h2',
          text: 'How to verify this inside ClarCV',
        },
        {
          type: 'p',
          text: 'Before analysis you will see a consent checkbox that names OpenAI and states the purpose (analysis or improvement), not training. The footer links the privacy policy, including the AI section. If something here does not match what you see, email privacy@clarcv.com. We prefer an awkward question to vague copy.',
        },
        {
          type: 'h2',
          text: 'If you are data-sensitive (ClarCV or any tool)',
        },
        {
          type: 'ul',
          items: [
            'A job CV rarely needs national ID, exact home address, or social-security numbers.',
            'A photo is optional in many markets; skip it if it adds nothing.',
            'Use a PDF or Word with selectable text, not a scan of a paper full of extra data.',
            'Read consent. If a site will not say who receives the CV, do not upload a file with your phone and email.',
            'Keep your own original. The tool is an editor, not the only copy of your career.',
          ],
        },
        {
          type: 'h2',
          text: 'A calm way to decide',
        },
        {
          type: 'p',
          text: 'ClarCV sends the CV to OpenAI to do the job you asked for. We do not sell it. We do not authorize training use. We keep account data so edit → template → PDF can exist. That is “safe” as in transparent and scoped, not magic: the internet still involves a provider. If that trade-off is not for you, do not upload; the templates and guides remain useful without AI.',
        },
        {
          type: 'faq',
          items: [
            {
              q: 'Does OpenAI train ChatGPT on my CV?',
              a: 'That is not authorized. We use the API, with training-sharing off, plus a DPA. Content is for your analysis or rewrite, not to train general models.',
            },
            {
              q: 'Does ClarCV sell my data?',
              a: 'No. We do not sell personal data or résumé content.',
            },
            {
              q: 'Is the file deleted the moment analysis ends?',
              a: 'Not necessarily. We keep it in your account so you can edit and download. You can request deletion at privacy@clarcv.com.',
            },
            {
              q: 'Can I use ClarCV without sending the CV to AI?',
              a: 'Yes: do not tick consent and do not start analysis. Creating from scratch or reading guides does not require an OpenAI call.',
            },
            {
              q: 'Where is the legal text?',
              a: 'In the Privacy Policy (AI section) and the Terms of Use. This guide is the plain explanation; the OpenAI contract is the processor DPA.',
            },
          ],
        },
        { type: 'cta' },
      ],
    },
  },
];
